import { Stack } from '@mantine/core';
import type { NumberChartConfig } from '@repo/api-codegen';
import { Text } from '@repo/foundations';
import { useMemo } from 'react';
import { formatValue } from '../../../utils/charts/utils';

// Aggregation functions
function aggregateValues(
	values: (number | string | Date | null)[],
	aggregateFunction: string | null | undefined
): number | string | Date | null {
	const numericValues = values
		.map((v) => {
			if (typeof v === 'number') return v;
			if (typeof v === 'string') {
				const parsed = parseFloat(v);
				return isNaN(parsed) ? null : parsed;
			}
			return null;
		})
		.filter((v): v is number => v !== null);

	switch (aggregateFunction) {
		case 'sum':
			return numericValues.reduce((acc, val) => acc + val, 0);
		case 'average':
			return numericValues.length > 0
				? numericValues.reduce((acc, val) => acc + val, 0) /
						numericValues.length
				: 0;
		case 'median':
			if (numericValues.length === 0) return 0;
			const sorted = [...numericValues].sort((a, b) => a - b);
			const mid = Math.floor(sorted.length / 2);
			return sorted.length % 2 === 0
				? (sorted[mid - 1] + sorted[mid]) / 2
				: sorted[mid];
		case 'max':
			return numericValues.length > 0 ? Math.max(...numericValues) : 0;
		case 'min':
			return numericValues.length > 0 ? Math.min(...numericValues) : 0;
		case 'count':
			return values.filter((v) => v !== null && v !== undefined).length;
		case 'first':
			return values.find((v) => v !== null && v !== undefined) ?? null;
		default:
			// If no aggregate specified, return the first value or sum for numbers
			if (numericValues.length > 0) {
				return numericValues.reduce((acc, val) => acc + val, 0);
			}
			return values.find((v) => v !== null && v !== undefined) ?? null;
	}
}

export function BiMetricSingleNumberChart({
	results,
	chartConfig,
}: {
	results: Record<string, any>[];
	chartConfig: NumberChartConfig;
}) {
	const { formattedValue } = useMemo(() => {
		if (!results || results.length === 0) {
			return {
				metricValue: null,
				formattedValue: '—',
				header: '',
				subHeader: '',
			};
		}

		// Get the main metric values
		const metricValues = results.map(
			(result) => result[chartConfig.numberColumnName]
		);

		// Apply aggregation if specified
		const aggregatedValue = aggregateValues(
			metricValues,
			chartConfig.numberValueAggregate
		);

		// Format the value using the column format configuration
		const columnFormat =
			chartConfig.columnConfigs?.[chartConfig.numberColumnName];
		const formatted = formatValue(aggregatedValue, columnFormat);

		// Get header and sub-header text
		const headerText = chartConfig.numberHeader;
		const subHeaderText = chartConfig.numberSubHeader;

		return {
			metricValue: aggregatedValue,
			formattedValue: formatted,
			header: headerText,
			subHeader: subHeaderText,
		};
	}, [results, chartConfig]);

	return (
		<Stack w="100%" h="100%" align="center" justify="center">
			<Stack align="center" spacing="xs" ta="center">
				<Text size="4xl" weight="bold" color="text/primary/default">
					{formattedValue}
				</Text>
			</Stack>
		</Stack>
	);
}
